import {memo, useRef, useCallback, useEffect} from 'react'
import {useConversationMessages} from '@/features/conversations/query/useConversationMessages'
import {useSendConversationMessage} from '@/features/conversations/query/useSendConversationMessage'
import {useMarkConversationAsReadOnOpen} from '@/features/conversations/hooks/useMarkConversationAsReadOnOpen'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from 'react-i18next'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {StyledMessagesContainer, StyledMessagesScrollArea, StyledMessagesBody, StyledLoadMoreIndicator} from './style'
import {MessageGroup} from '../message-group/MessageGroup'
import {EmptyConversationState} from '../empty-conversation-state/EmptyConversationState'
import {ConversationMessagesSkeleton} from '../conversation-messages-skeleton/ConversationMessagesSkeleton'
import {NewMessageForm} from '../new-message-form/NewMessageForm'

const ConversationMessagesComponent = ({
    conversationId,
    hasUnreadMessages,
    patientId
}: {
    conversationId: string
    hasUnreadMessages?: boolean | null
    patientId: string
}) => {
    const {t} = useTranslation()
    const {messages, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage} =
        useConversationMessages(conversationId)
    const {mutate: sendMessage, isPending: isSending} = useSendConversationMessage()
    const scrollViewportRef = useRef<HTMLDivElement>(null)
    const observerTarget = useRef<HTMLDivElement>(null)

    // Automatically mark conversation as read when opened with unread messages
    useMarkConversationAsReadOnOpen(patientId, conversationId, hasUnreadMessages)

    const handleIntersection = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries
            if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                fetchNextPage()
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage]
    )

    // Set up intersection observer for infinite scrolling at the top
    useEffect(() => {
        const observer = new IntersectionObserver(handleIntersection, {
            root: null,
            rootMargin: '20px',
            threshold: 0.1
        })

        if (observerTarget.current) {
            observer.observe(observerTarget.current)
        }

        return () => {
            observer.disconnect()
        }
    }, [handleIntersection])

    // Auto-scroll to bottom when conversation opens or new messages arrive
    useEffect(() => {
        if (scrollViewportRef.current && messages && messages.length > 0) {
            scrollViewportRef.current.scrollTop = scrollViewportRef.current.scrollHeight
        }
    }, [messages])

    const handleSendMessage = (data: {message: string; isUrgent: boolean}) => {
        sendMessage({conversationId, message: data.message, isUrgent: data.isUrgent})
    }

    return (
        <StyledMessagesContainer direction="column" fullWidth>
            <StyledMessagesScrollArea>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />} viewportProps={{ref: scrollViewportRef}}>
                    <StyledMessagesBody gap={2} direction="column-reverse" justify="flex-start">
                        {isLoading ? (
                            <ConversationMessagesSkeleton />
                        ) : (
                            <>
                                {hasNextPage && (
                                    <StyledLoadMoreIndicator ref={observerTarget}>
                                        {isFetchingNextPage ? (
                                            <Flexbox align="center" justify="center" gap={2}>
                                                <Spinner size={16} />
                                                {t('conversations:loading_more')}
                                            </Flexbox>
                                        ) : (
                                            t('conversations:scroll_up_for_more')
                                        )}
                                    </StyledLoadMoreIndicator>
                                )}

                                {messages && messages.length > 0 ? (
                                    <MessageGroup messages={messages} />
                                ) : (
                                    <EmptyConversationState />
                                )}
                            </>
                        )}
                    </StyledMessagesBody>
                </ScrollArea>
            </StyledMessagesScrollArea>

            <NewMessageForm onSubmit={handleSendMessage} isLoading={isLoading} isSending={isSending} />
        </StyledMessagesContainer>
    )
}

export const ConversationMessages = memo(ConversationMessagesComponent)
